<template>
    <!-- Header for the Active groups KPI detail modals (Weight/Water/Mortality), rendered by the
         shared DetailModalShell: bold section name + muted meta line, with prev/next navigation
         (display order of the page's clickable cells) grouped with close. Each navigation button
         names its destination; a pair of arrows alone makes switching sections needlessly opaque.
         Renders nothing while `section` is null. -->
    <div v-if="section" class="flex items-start gap-3 px-5 pb-3 pt-4">
        <div class="min-w-0 flex-1">
            <h2 class="truncate text-[17px] font-extrabold leading-tight text-gray-900">
                {{ section.sectionName }}
            </h2>
            <p class="mt-0.5 truncate text-xs text-gray-400">{{ metaLabel }}</p>
            <!-- Same red alarm chip as the table's Alder column; hands the section back to the
                 page, which runs its existing alarm-handling flow. -->
            <button
                v-if="section.activeAlarms.length > 0"
                type="button"
                class="mt-1.5"
                :class="ALARM_CHIP_CLASS"
                @click="emit('handleAlarm', section)"
            >
                <AlertTriangle class="h-3 w-3 shrink-0" aria-hidden="true" />
                {{ $t('views.monitoring.activeGroups.alarmHandle.button') }}
                <ChevronRight class="h-3 w-3 shrink-0" aria-hidden="true" />
            </button>
        </div>
        <div class="flex items-center gap-1">
            <button
                type="button"
                class="flex max-w-24 items-center gap-1 rounded-lg px-1.5 py-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 sm:max-w-32 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                :disabled="!prevSection"
                :title="prevSection?.sectionName"
                @click="navigateTo(prevSection)"
            >
                <ChevronLeft class="h-4 w-4" aria-hidden="true" />
                <span v-if="prevSection" class="truncate text-xs font-semibold">{{ prevSection.sectionName }}</span>
            </button>
            <button
                type="button"
                class="flex max-w-24 items-center gap-1 rounded-lg px-1.5 py-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600 sm:max-w-32 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-transparent disabled:hover:text-gray-400"
                :disabled="!nextSection"
                :title="nextSection?.sectionName"
                @click="navigateTo(nextSection)"
            >
                <span v-if="nextSection" class="truncate text-xs font-semibold">{{ nextSection.sectionName }}</span>
                <ChevronRight class="h-4 w-4" aria-hidden="true" />
            </button>
            <button
                type="button"
                class="rounded-lg p-1 text-gray-400 transition-colors hover:bg-gray-200 hover:text-gray-600"
                @click="emit('close')"
            >
                <X class="h-4 w-4" aria-hidden="true" />
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue';
import { AlertTriangle, ChevronLeft, ChevronRight, X } from 'lucide-vue-next';
import { i18n } from '@/plugins/internationalization/i18n';
import { ALARM_CHIP_CLASS } from './alarmChipStyle';
import { isTopmostDetailSurface, popDetailSurface, pushDetailSurface } from './sectionDetailSurfaces';
import type { SectionRow } from './sectionRow';

const props = defineProps<{
    // The row this header describes (null = not open — the header renders nothing).
    section: SectionRow | null;
    // All rows whose cells can open the caller's detail view, in the page's display order — drives
    // prev/next navigation. The caller stays in control: navigation only emits the target row.
    sections: SectionRow[];
}>();

const emit = defineEmits<{
    close: [];
    navigate: [section: SectionRow];
    handleAlarm: [section: SectionRow];
}>();

// Position within the page's display order, matched by id so a refreshed row object still resolves.
const currentIndex = computed(() => (props.section ? props.sections.findIndex((row) => row.id === props.section!.id) : -1));
const prevSection = computed(() => (currentIndex.value > 0 ? props.sections[currentIndex.value - 1] : null));
const nextSection = computed(() => (currentIndex.value !== -1 && currentIndex.value < props.sections.length - 1 ? props.sections[currentIndex.value + 1] : null));

function navigateTo(target: SectionRow | null): void {
    if (target) emit('navigate', target);
}

// Muted meta line under the section name: farm · barn · age (mirrors the table's Alder chip).
const metaLabel = computed(() => {
    if (!props.section) return '';
    const parts = [props.section.farmName, props.section.barnName];
    if (props.section.day !== null) {
        parts.push(
            i18n.global.t('views.monitoring.activeGroups.texts.weekDay', {
                week: props.section.week,
                day: props.section.day,
            })
        );
    }
    return parts.filter(Boolean).join(' · ');
});

// Escape closes; the arrow keys mirror the prev/next buttons. Both are inert while the header is
// closed and while something inside the detail view has a prior claim on the key:
//
//  - an open dropdown or select. Dismissing a menu is not a request to close the whole view, and
//    arrowing through its options is not a request to be moved to another Section behind it.
//  - a focused text field. Its own caret movement and its own Escape come first.
//
// Registered in the capture phase, on window, rather than through onKeyStroke: the popup layers
// close themselves from their own capture-phase listener further down the tree, so a bubble-phase
// handler is only ever reached once the layer it was supposed to defer to has already gone.
function claimedByLayerAbove(event: KeyboardEvent): boolean {
    if (document.querySelector('[data-radix-popper-content-wrapper]')) return true;
    const target = event.target as HTMLElement | null;
    return target instanceof HTMLElement && (target.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName));
}

function onKeydownCapture(event: KeyboardEvent): void {
    if (!props.section || !isTopmostDetailSurface(surface) || claimedByLayerAbove(event)) return;
    if (event.key === 'Escape') emit('close');
    if (event.key === 'ArrowLeft') navigateTo(prevSection.value);
    if (event.key === 'ArrowRight') navigateTo(nextSection.value);
}

// Registered on open, released on close, so the shared registry's order matches the order the reader
// opened these surfaces in - see sectionDetailSurfaces.
let surface: symbol;

onMounted(() => {
    surface = pushDetailSurface();
    window.addEventListener('keydown', onKeydownCapture, true);
});

onUnmounted(() => {
    popDetailSurface(surface);
    window.removeEventListener('keydown', onKeydownCapture, true);
});
</script>
